import {
  formatAmountWithCommas,
  getLocalizedCurrency,
} from '@unified-checkout/design-system';

import type { ILabelValueProps } from '@app/dashboard/types/innerLayout/balance_and_transfers/transfers/summary';
import type { ISettlementLimitsParams } from '@app/dashboard/types/innerLayout/balance_and_transfers/balance/instant-settlement';
import type { IActiveBalance } from '@app/dashboard/types/innerLayout/balance_and_transfers/balance';
import { COUNTRIES, COUNTRIES_INFO } from '@app/dashboard/constants/countries';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { PARAMS } from '@app/dashboard/constants/balance_and_transfers';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { SEARCH_PARAMS_KEYS } from '@app/dashboard/constants/search-params-keys';

export const formatAmountWithCurrency = ({
  amount,
  currency,
  lang,
}: {
  amount: number;
  currency: string;
  lang: 'en' | 'ar';
}) => {
  const formattedAmount = formatAmountWithCommas({ amount, currency });
  const localizedCurrency = getLocalizedCurrency(lang, currency);
  const isLTR = lang === 'en';

  return isLTR
    ? `${localizedCurrency} ${formattedAmount}`
    : `${formattedAmount} ${localizedCurrency}`;
};

export const getValueLabelObject = (item?: string | ILabelValueProps) => {
  if (!item || typeof item === 'string') return {};
  return item;
};

export const getCurrencyFlag = (currency: string) => {
  const country = Object.values(COUNTRIES_INFO).find(
    (countryInfo) => countryInfo.currency === currency
  );
  return country?.flag;
};

export const getSettlementLimits = ({
  targetValue,
  settlementBalanceMinLimit,
  settlementBalanceMaxLimit,
  selectedBalance,
}: ISettlementLimitsParams) => {
  const value = Number(targetValue);
  const available = Number(selectedBalance);

  return {
    isMaxAvailableSettlement: value > available,
    isBelowMinAllowedSettlement: value < settlementBalanceMinLimit,
    isAboveMaxAllowedSettlement: value > settlementBalanceMaxLimit,
  };
};

export const isUAECountry = () => {
  return getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY) === COUNTRIES.ARE;
};

export const isEGYCountry = () => {
  return getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY) === COUNTRIES.EGY;
};

export const bankAccountNumberMask = (
  accountNumber: string | { bank_name?: string; account_number?: string }
) => {
  const destination =
    typeof accountNumber === 'string'
      ? accountNumber
      : `${accountNumber?.bank_name}. ****${accountNumber?.account_number}`;

  return destination;
};

export const getTargetAccount = (
  activeBalance: IActiveBalance | null,
  searchParams: URLSearchParams
) => {
  const accountIdFromUrl = searchParams.get(PARAMS.ACCOUNT);

  const accountId = accountIdFromUrl
    ? Number(accountIdFromUrl)
    : activeBalance?.account?.id ?? '';

  const haveAccounts = activeBalance?.haveAccounts;

  return { accountId, haveAccounts };
};

export const viewRelatedTransactions = (transferId: string) => {
  const targetId = transferId.replace('#TRF-', '');

  window.open(
    `${INNER_ROUTES.TRANSACTIONS}?${SEARCH_PARAMS_KEYS.TRANSFER_ID}=${targetId}`,
    '_blank'
  );
};
